import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { platformApi, TenantDetail, BrandingConfig, TenantAdminDto, PlatformStats, TenantStats, MonthlyCount, FeatureFlagsConfig, TenantConfigDto } from '../../services/api';
import { Btn, C, Card } from '../../components/UI';
import MarketplaceCatalogAdmin from './marketplace/MarketplaceCatalogAdmin';
import TenantTemplateCatalogAdmin from './templates/TenantTemplateCatalogAdmin';
import TemplatePickerModal from './templates/TemplatePickerModal';
import ActiveSessionsPanel from './ActiveSessionsPanel';
import IntegrationsPanel from './IntegrationsPanel';

// ── Shared helpers ────────────────────────────────────────────────────────────

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <label style={{ flex: 1, fontSize: 13, color: C.textSub }}>{label}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="color" value={value} onChange={e => onChange(e.target.value)}
                    style={{ width: 36, height: 36, border: 'none', borderRadius: 6, cursor: 'pointer', padding: 2 }} />
                <input type="text" value={value} onChange={e => onChange(e.target.value)}
                    style={{ width: 90, padding: '6px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontFamily: 'monospace', color: C.text }} />
            </div>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, type = 'text' }: {
    label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string;
}) {
    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: C.textSub, marginBottom: 5 }}>{label}</label>
            <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, color: C.text, boxSizing: 'border-box' }} />
        </div>
    );
}

function ErrorBox({ msg }: { msg: string }) {
    return <div style={{ color: '#D84040', fontSize: 13, padding: '8px 12px', background: '#FFF0F0', borderRadius: 6, marginBottom: 12 }}>{msg}</div>;
}

// ── Stat tile ─────────────────────────────────────────────────────────────────

function StatTile({ label, value }: { label: string; value: string | number }) {
    return (
        <div style={{ background: C.bg, borderRadius: 8, padding: '10px 14px', flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{value}</div>
        </div>
    );
}

// ── Monthly bar chart ─────────────────────────────────────────────────────────

function MonthlyChart({ data }: { data: MonthlyCount[] }) {
    const [hovered, setHovered] = useState<number | null>(null);
    if (!data || data.length === 0) {
        return <div style={{ color: C.textMuted, fontSize: 13, padding: '8px 0' }}>No job data yet.</div>;
    }
    const maxJobs = Math.max(...data.map(d => d.jobCount), 1);
    const W = 500, H = 130, PX = 12, PT = 16, PB = 28;
    const plotH = H - PT - PB;
    const slotW = (W - PX * 2) / data.length;
    const barW = slotW * 0.6;
    const fmt$ = (v: number) => '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });

    return (
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
            {[0.33, 0.66, 1].map(f => (
                <line key={f} x1={PX} x2={W - PX}
                    y1={PT + plotH * (1 - f)} y2={PT + plotH * (1 - f)}
                    stroke={C.border} strokeWidth="1" strokeDasharray="3,3" />
            ))}
            {data.map((d, i) => {
                const barH = (d.jobCount / maxJobs) * plotH;
                const slotX = PX + i * slotW;
                const barX = slotX + (slotW - barW) / 2;
                const barY = PT + plotH - barH;
                const midX = slotX + slotW / 2;
                const isHov = hovered === i;
                const shortMonth = d.month.slice(0, 3);
                const tipX = Math.min(barX - 4, W - 94);

                return (
                    <g key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} style={{ cursor: 'default' }}>
                        <rect x={slotX} y={PT} width={slotW} height={plotH + PB} fill="transparent" />
                        <rect x={barX} y={barH > 0 ? barY : PT + plotH - 2}
                            width={barW} height={Math.max(barH, 2)}
                            rx="3" ry="3"
                            fill={isHov ? C.greenMid : C.green} opacity={isHov ? 1 : 0.78} />
                        <text x={midX} y={H - 6} textAnchor="middle" fill={C.textMuted}
                            fontSize="9.5" fontFamily="Inter, system-ui, sans-serif">
                            {shortMonth}
                        </text>
                        {isHov && (
                            <g>
                                <rect x={tipX} y={barY - 44} width={88} height={36}
                                    rx="5" fill={C.card} stroke={C.border} strokeWidth="1" />
                                <text x={tipX + 8} y={barY - 26} fill={C.text}
                                    fontSize="10.5" fontFamily="Inter, system-ui, sans-serif" fontWeight="600">
                                    {d.jobCount} {d.jobCount === 1 ? 'job' : 'jobs'}
                                </text>
                                <text x={tipX + 8} y={barY - 11} fill={C.textSub}
                                    fontSize="10" fontFamily="Inter, system-ui, sans-serif">
                                    {fmt$(d.revenue)}
                                </text>
                            </g>
                        )}
                    </g>
                );
            })}
        </svg>
    );
}

// ── Per-tenant stats panel ────────────────────────────────────────────────────

function TenantStatsPanel({ tenantId }: { tenantId: string }) {
    const [stats, setStats] = useState<TenantStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        platformApi.getTenantStats(tenantId)
            .then(setStats)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [tenantId]);

    const fmt$ = (v: number) => '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });

    return (
        <Card>
            <div style={{ fontSize: 12, color: C.textMuted, fontWeight: 600, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Analytics</div>
            {loading && <div style={{ color: C.textMuted, fontSize: 13 }}>Loading...</div>}
            {!loading && stats && (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
                        {[
                            { label: 'Total Jobs', value: stats.totalJobs },
                            { label: 'Revenue', value: fmt$(stats.totalRevenue) },
                            { label: 'Active Contractors', value: stats.activeContractors },
                            { label: 'Customers', value: stats.totalCustomers },
                        ].map(s => (
                            <div key={s.label} style={{ background: C.bg, borderRadius: 8, padding: '10px 12px' }}>
                                <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{s.label}</div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{s.value}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 6 }}>Jobs — last 6 months</div>
                    <MonthlyChart data={stats.jobsByMonth} />
                </>
            )}
            {!loading && !stats && (
                <div style={{ color: C.textMuted, fontSize: 13 }}>Could not load analytics.</div>
            )}
        </Card>
    );
}

// ── New Tenant Modal ──────────────────────────────────────────────────────────

function NewTenantModal({ onCreated, onClose }: { onCreated: (t: TenantDetail) => void; onClose: () => void }) {
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [domain, setDomain] = useState('');
    const [supportEmail, setSupportEmail] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const autoSlug = (n: string) => n.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

    const handleNameChange = (v: string) => {
        setName(v);
        if (!slug || slug === autoSlug(name)) setSlug(autoSlug(v));
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!slug.match(/^[a-z0-9-]+$/)) { setError('Slug may only contain lowercase letters, numbers, and hyphens.'); return; }
        setSaving(true); setError('');
        try {
            const t = await platformApi.createTenant({ name, slug, domain: domain || null, supportEmail: supportEmail || null });
            onCreated(t);
        } catch (err: any) {
            setError(err.message || 'Failed to create tenant.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: C.card, borderRadius: 16, padding: 32, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>New Tenant</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.textMuted, lineHeight: 1 }}>×</button>
                </div>
                <form onSubmit={submit}>
                    <Field label="Company / tenant name *" value={name} onChange={handleNameChange} placeholder="Acme Junk Removal" />
                    <div style={{ marginBottom: 14 }}>
                        <label style={{ display: 'block', fontSize: 13, color: C.textSub, marginBottom: 5 }}>
                            Slug * <span style={{ color: C.textMuted, fontWeight: 400 }}>— subdomain on box-runner.net</span>
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                            <input value={slug} onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                placeholder="acme" required
                                style={{ flex: 1, padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: '8px 0 0 8px', fontSize: 14, color: C.text, minWidth: 0 }} />
                            <span style={{ padding: '8px 12px', background: C.bg, border: `1px solid ${C.border}`, borderLeft: 'none', borderRadius: '0 8px 8px 0', fontSize: 13, color: C.textMuted, whiteSpace: 'nowrap' }}>.box-runner.net</span>
                        </div>
                    </div>
                    <Field label="Custom domain (optional)" value={domain} onChange={setDomain} placeholder="app.acmejunk.com" />
                    <Field label="Support email (optional)" value={supportEmail} onChange={setSupportEmail} placeholder="support@acmejunk.com" type="email" />
                    {error && <ErrorBox msg={error} />}
                    <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                        <Btn variant="outline" onClick={onClose} type="button">Cancel</Btn>
                        <Btn type="submit" disabled={saving} fullWidth>{saving ? 'Creating...' : 'Create tenant'}</Btn>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── Tenant row ────────────────────────────────────────────────────────────────

function CountChip({ label, value, bg, color }: { label: string; value: number; bg: string; color: string }) {
    return (
        <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 10, background: bg, color, whiteSpace: 'nowrap' }}>
            {value} {label}
        </span>
    );
}

function TenantRow({ tenant, onSelect }: { tenant: TenantDetail; onSelect: () => void }) {
    return (
        <div onClick={onSelect} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }}
            onMouseEnter={e => (e.currentTarget.style.background = C.bg)}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: tenant.branding.logoUrl ? 'transparent' : tenant.branding.primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                {tenant.branding.logoUrl
                    ? <img src={tenant.branding.logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : <span style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>{tenant.name[0].toUpperCase()}</span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{tenant.name}</div>
                <div style={{ fontSize: 12, color: C.textMuted }}>{tenant.slug}.box-runner.net{tenant.domain ? ` · ${tenant.domain}` : ''}</div>
            </div>
            {tenant.jobCount !== undefined && (
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <CountChip label="jobs" value={tenant.jobCount} bg="#EEF2FF" color="#4338CA" />
                    <CountChip label="contr" value={tenant.contractorCount ?? 0} bg="#FDF4FF" color="#7C3AED" />
                    <CountChip label="cust" value={tenant.customerCount ?? 0} bg={C.greenBg} color={C.green} />
                </div>
            )}
            <div style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: tenant.isActive ? C.greenBg : '#F5F5F5', color: tenant.isActive ? C.green : C.textMuted, flexShrink: 0 }}>
                {tenant.isActive ? 'Active' : 'Inactive'}
            </div>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke={C.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
    );
}

// ── Admins panel ──────────────────────────────────────────────────────────────

function AdminsPanel({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
    const [admins, setAdmins] = useState<TenantAdminDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        platformApi.getTenantAdmins(tenantId)
            .then(setAdmins)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [tenantId]);

    const createAdmin = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true); setError(''); setSuccess('');
        try {
            const admin = await platformApi.createTenantAdmin(tenantId, { name, email, password: password || undefined });
            setAdmins(prev => [...prev, admin]);
            setName(''); setEmail(''); setPassword('');
            setShowForm(false);
            setSuccess(`Admin account created — credentials sent to ${admin.email}`);
        } catch (err: any) {
            setError(err.message || 'Failed to create admin.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Admins</h3>
                {!showForm && <Btn size="sm" onClick={() => setShowForm(true)}>+ Add admin</Btn>}
            </div>

            {success && <div style={{ color: C.green, fontSize: 13, padding: '8px 12px', background: C.greenBg, borderRadius: 6, marginBottom: 12 }}>{success}</div>}

            {showForm && (
                <form onSubmit={createAdmin} style={{ background: C.bg, borderRadius: 8, padding: 16, marginBottom: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <div>
                            <label style={{ display: 'block', fontSize: 12, color: C.textSub, marginBottom: 4 }}>Name *</label>
                            <input required value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith"
                                style={{ width: '100%', padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, color: C.text, boxSizing: 'border-box' }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: 12, color: C.textSub, marginBottom: 4 }}>Email *</label>
                            <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@acme.com"
                                style={{ width: '100%', padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, color: C.text, boxSizing: 'border-box' }} />
                        </div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 12, color: C.textSub, marginBottom: 4 }}>
                            Password <span style={{ color: C.textMuted, fontWeight: 400 }}>(leave blank to auto-generate)</span>
                        </label>
                        <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Auto-generated"
                            style={{ width: '100%', padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, color: C.text, boxSizing: 'border-box' }} />
                    </div>
                    {error && <ErrorBox msg={error} />}
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Btn type="submit" size="sm" disabled={saving}>{saving ? 'Creating...' : 'Create & send email'}</Btn>
                        <Btn variant="outline" size="sm" type="button" onClick={() => { setShowForm(false); setError(''); }}>Cancel</Btn>
                    </div>
                </form>
            )}

            {loading && <div style={{ color: C.textMuted, fontSize: 13 }}>Loading...</div>}

            {!loading && admins.length === 0 && !showForm && (
                <div style={{ color: C.textMuted, fontSize: 13, padding: '8px 0' }}>No admins yet. Add one to let this tenant log in.</div>
            )}

            {admins.map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${C.borderLight}` }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: C.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ color: C.green, fontSize: 13, fontWeight: 700 }}>{a.name[0].toUpperCase()}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{a.name}</div>
                        <div style={{ fontSize: 12, color: C.textMuted }}>{a.email}</div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: a.isActive ? C.greenBg : '#F5F5F5', color: a.isActive ? C.green : C.textMuted }}>
                        {a.isActive ? 'Active' : 'Inactive'}
                    </div>
                </div>
            ))}
        </Card>
    );
}

// ── Feature flags panel ───────────────────────────────────────────────────────

const FLAG_LABELS: { key: keyof FeatureFlagsConfig; label: string; description: string }[] = [
    { key: 'dumpsterRental',          label: 'Dumpster Rental',          description: 'Dumpster booking, vendor marketplace' },
    { key: 'loadingJobs',             label: 'Loading Jobs',             description: 'Contractor loading crew for dumpster orders' },
    { key: 'aiEstimation',            label: 'AI Estimation',            description: 'Photo-based unit estimation at booking' },
    { key: 'priceAdjustments',        label: 'Price Adjustments',        description: 'Contractor can request adjusted price after job' },
    { key: 'tipping',                 label: 'Tipping',                  description: 'Customer can add a tip at checkout' },
    { key: 'ratings',                 label: 'Ratings',                  description: 'Post-job star ratings and reviews' },
    { key: 'inAppChat',               label: 'In-App Chat',              description: 'Contractor ↔ customer messaging on active jobs' },
    { key: 'backgroundCheckRequired', label: 'Background Check Required', description: 'Contractors must pass Checkr before accepting jobs' },
    // HVAC-driven flags (TREK-131) — default OFF, enabled per-tenant (Fobbus AC) as each piece ships. See TREK-133.
    { key: 'serviceCatalogPricing',   label: 'Service Catalog Pricing',   description: 'Itemized catalog/labor/parts pricing instead of flat-rate (TREK-132)' },
    { key: 'estimatesAndQuotes',      label: 'Estimates & Quotes',       description: 'Quote-before-work approval flow (TREK-78)' },
    { key: 'partialPayments',         label: 'Partial Payments',         description: 'Deposit / progress / final milestone billing (TREK-80)' },
    { key: 'contractorCertTracking',  label: 'Contractor Cert Tracking', description: 'Skills and certification fields on contractor profile (TREK-75)' },
    { key: 'digitalWorkOrders',       label: 'Digital Work Orders',      description: 'Signature capture on job completion (TREK-88)' },
    { key: 'inventoryTracking',       label: 'Inventory Tracking',       description: 'Parts/supply tracking with reorder alerts (TREK-81)' },
    { key: 'aiWarrantyLookup',        label: 'AI Warranty Lookup',       description: 'Nameplate photo → AI-decoded warranty status (TREK-105)' },
    { key: 'serviceAreaMapAndLicenses', label: 'Service Area Map + Business Licenses', description: 'Map-assisted radius/county rule creation and business license compliance tracking' },
    { key: 'estimateBuilder', label: 'Estimate Builder', description: 'Typed line items, branded PDF with photos, e-signature, and tokenized public estimate links' },
    { key: 'propertyRecords', label: 'Property Records', description: 'Persistent address identity — see job history across everything that ever happened at one property' },
    { key: 'complianceDocTracking', label: 'Compliance Document Tracking', description: 'Generic license/insurance/registration upload tracking with expiry, for any contractor, vendor, or property' },
    { key: 'photoChecklists', label: 'Photo Checklists', description: 'Require a named set of photos (front exterior, each room, ...) before a job can be marked complete' },
    { key: 'jobSlaTracking', label: 'Job SLA Tracking', description: 'Due dates and priority per job, with an at-risk/overdue view in Admin' },
    { key: 'clientPortal', label: 'Client Portal', description: 'Read-only external login for the tenant\'s own B2B client to see their own properties' },
    { key: 'donationCenters', label: 'Donation Centers', description: 'Customer picks a donation center at booking for Donation Run-type jobs; emails a receipt PDF on completion' },
    { key: 'documentShredding', label: 'Document Shredding', description: 'Compliant document destruction: structured document-category declaration/confirmation, QR-tracked bins, and an emailed Certificate of Destruction on completion' },
];

function FeatureFlagsPanel({ tenantId }: { tenantId: string }) {
    const [flags, setFlags] = useState<FeatureFlagsConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        setLoading(true);
        platformApi.getTenantConfig(tenantId)
            .then(cfg => setFlags(cfg.features))
            .catch(() => setError('Could not load feature flags.'))
            .finally(() => setLoading(false));
    }, [tenantId]);

    const toggle = (key: keyof FeatureFlagsConfig) => {
        if (!flags) return;
        const next = !flags[key];
        const updated = { ...flags, [key]: next };
        // Document Shredding's provider-certification tracking (NAID AAA, ISO 9001/27001,
        // e-Stewards) is built on the generic ComplianceDocument feature, not its own storage —
        // turning Shredding on without it just leaves the admin unable to find the Compliance
        // Documents panel at all. Bring it along automatically rather than requiring a second,
        // seemingly-unrelated flag to be discovered separately. One-way: turning Shredding back
        // off doesn't turn Compliance Documents off too, since other things may depend on it.
        if (key === 'documentShredding' && next) updated.complianceDocTracking = true;
        setFlags(updated);
        setSuccess('');
    };

    const save = async () => {
        if (!flags) return;
        setSaving(true); setError(''); setSuccess('');
        try {
            await platformApi.updateTenantConfig(tenantId, { features: flags });
            setSuccess('Feature flags saved.');
        } catch (err: any) {
            setError(err.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Feature Flags</h3>
                {flags && <Btn size="sm" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>}
            </div>

            {loading && <div style={{ color: C.textMuted, fontSize: 13 }}>Loading...</div>}
            {error && <ErrorBox msg={error} />}
            {success && <div style={{ color: C.green, fontSize: 13, padding: '8px 12px', background: C.greenBg, borderRadius: 6, marginBottom: 12 }}>{success}</div>}

            {flags && FLAG_LABELS.map(({ key, label, description }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0', borderBottom: `1px solid ${C.borderLight}` }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</div>
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 1 }}>{description}</div>
                    </div>
                    <button
                        onClick={() => toggle(key)}
                        style={{
                            width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                            background: flags[key] ? C.green : '#D1D5DB',
                            position: 'relative', flexShrink: 0, transition: 'background 0.15s',
                        }}
                        title={flags[key] ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                    >
                        <span style={{
                            position: 'absolute', top: 3, left: flags[key] ? 21 : 3,
                            width: 16, height: 16, borderRadius: '50%', background: '#fff',
                            transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        }} />
                    </button>
                    <span style={{ fontSize: 11, fontWeight: 600, color: flags[key] ? C.green : C.textMuted, width: 36, textAlign: 'right' }}>
                        {flags[key] ? 'On' : 'Off'}
                    </span>
                </div>
            ))}
        </Card>
    );
}

// ── Branding panel ────────────────────────────────────────────────────────────

function BrandingPanel({ tenant, onSaved }: { tenant: TenantDetail; onSaved: (u: TenantDetail) => void }) {
    const [primary, setPrimary] = useState(tenant.branding.primaryColor);
    const [secondary, setSecondary] = useState(tenant.branding.secondaryColor);
    const [accent, setAccent] = useState(tenant.branding.accentColor ?? tenant.branding.secondaryColor);
    const [buttonColor, setButtonColor] = useState(tenant.branding.buttonColor ?? tenant.branding.secondaryColor);
    const [navTextColor, setNavTextColor] = useState(tenant.branding.navTextColor ?? '#ffffff');
    const [appName, setAppName] = useState(tenant.branding.appName);
    const [serviceNoun, setServiceNoun] = useState(tenant.branding.serviceNoun ?? '');
    const [serviceDescription, setServiceDescription] = useState(tenant.branding.serviceDescription ?? '');
    const [contractorNoun, setContractorNoun] = useState(tenant.branding.contractorNoun ?? '');
    const [logoUrl, setLogoUrl] = useState(tenant.branding.logoUrl ?? '');
    const [logoLinkUrl, setLogoLinkUrl] = useState(tenant.branding.logoLinkUrl ?? '');
    const [splashUrl, setSplashUrl] = useState(tenant.branding.splashImageUrl ?? '');
    const [legalName, setLegalName] = useState(tenant.businessInfo.businessLegalName ?? '');
    const [addressLine1, setAddressLine1] = useState(tenant.businessInfo.businessAddressLine1 ?? '');
    const [addressLine2, setAddressLine2] = useState(tenant.businessInfo.businessAddressLine2 ?? '');
    const [bizCity, setBizCity] = useState(tenant.businessInfo.businessCity ?? '');
    const [bizState, setBizState] = useState(tenant.businessInfo.businessState ?? '');
    const [bizPostalCode, setBizPostalCode] = useState(tenant.businessInfo.businessPostalCode ?? '');
    const [bizPhone, setBizPhone] = useState(tenant.businessInfo.businessPhone ?? '');
    const [taxId, setTaxId] = useState(tenant.businessInfo.taxId ?? '');
    const [disclaimerText, setDisclaimerText] = useState(tenant.businessInfo.defaultContractDisclaimerText ?? '');
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadingSplash, setUploadingSplash] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const fileRef = useRef<HTMLInputElement>(null);
    const splashRef = useRef<HTMLInputElement>(null);

    const save = async () => {
        setSaving(true); setError(''); setSuccess('');
        try {
            const updated = await platformApi.updateBranding(tenant.id, {
                primaryColor: primary, secondaryColor: secondary,
                accentColor: accent, buttonColor, navTextColor,
                logoUrl: logoUrl || null, appName, faviconUrl: null,
                logoLinkUrl: logoLinkUrl.trim() || null,
                splashImageUrl: splashUrl || null,
                serviceNoun: serviceNoun.trim() || null,
                serviceDescription: serviceDescription.trim() || null,
                contractorNoun: contractorNoun.trim() || null,
                businessLegalName: legalName.trim() || null,
                businessAddressLine1: addressLine1.trim() || null,
                businessAddressLine2: addressLine2.trim() || null,
                businessCity: bizCity.trim() || null,
                businessState: bizState.trim() || null,
                businessPostalCode: bizPostalCode.trim() || null,
                businessPhone: bizPhone.trim() || null,
                taxId: taxId.trim() || null,
                defaultContractDisclaimerText: disclaimerText.trim() || null,
            });
            onSaved(updated);
            setSuccess('Branding saved.');
        } catch (e: any) {
            setError(e.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const uploadLogo = async (file: File) => {
        setUploading(true); setError('');
        try {
            const { logoUrl: url } = await platformApi.uploadLogo(tenant.id, file);
            setLogoUrl(url);
        } catch (e: any) {
            setError(e.message || 'Upload failed.');
        } finally {
            setUploading(false);
        }
    };

    const uploadSplash = async (file: File) => {
        setUploadingSplash(true); setError('');
        try {
            const { splashImageUrl: url } = await platformApi.uploadSplash(tenant.id, file);
            setSplashUrl(url);
        } catch (e: any) {
            setError(e.message || 'Upload failed.');
        } finally {
            setUploadingSplash(false);
        }
    };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 240px', gap: 20, alignItems: 'start' }}>
            <Card>
                <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600 }}>Branding</h3>
                <Field label="App name" value={appName} onChange={setAppName} />

                <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, marginTop: 4 }}>Service language</div>
                <Field label='Service noun (e.g. "pickup" — leave blank for generic "service")' value={serviceNoun} onChange={setServiceNoun} />
                <Field label='Home card description (leave blank for generic fallback)' value={serviceDescription} onChange={setServiceDescription} />
                <Field label='Contractor noun (e.g. "Runner" — leave blank for generic "Contractor"; display text only, not roles/database)' value={contractorNoun} onChange={setContractorNoun} />

                <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, marginTop: 4 }}>Colors</div>
                <ColorField label="Brand color — sidebar &amp; header background" value={primary} onChange={setPrimary} />
                <ColorField label="Secondary — gradient &amp; fallback" value={secondary} onChange={setSecondary} />
                <ColorField label="Accent — active nav, badges, avatar" value={accent} onChange={setAccent} />
                <ColorField label="Button — primary action buttons" value={buttonColor} onChange={setButtonColor} />
                <ColorField label="Nav text — text &amp; icons on sidebar/header" value={navTextColor} onChange={setNavTextColor} />
                <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, color: C.textSub, marginBottom: 6 }}>Logo URL</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="https://..."
                            style={{ flex: 1, padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.text }} />
                        <Btn variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? '...' : 'Upload'}</Btn>
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }}
                            onChange={e => { const f = e.target.files?.[0]; if (f) uploadLogo(f); }} />
                    </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, color: C.textSub, marginBottom: 4 }}>Logo click URL</label>
                    <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>When set, clicking the header logo in the web app opens this URL in a new tab (e.g. the tenant's marketing site). Leave blank for no link.</div>
                    <input value={logoLinkUrl} onChange={e => setLogoLinkUrl(e.target.value)} placeholder="https://www.example.com"
                        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.text, boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, color: C.textSub, marginBottom: 4 }}>Login screen image</label>
                    <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>Displayed as the right-panel background on the login page. If not set, tenant colors are used instead.</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input value={splashUrl} onChange={e => setSplashUrl(e.target.value)} placeholder="https://..."
                            style={{ flex: 1, padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.text }} />
                        <Btn variant="outline" size="sm" onClick={() => splashRef.current?.click()} disabled={uploadingSplash}>{uploadingSplash ? '...' : 'Upload'}</Btn>
                        <input ref={splashRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }}
                            onChange={e => { const f = e.target.files?.[0]; if (f) uploadSplash(f); }} />
                    </div>
                    {splashUrl && (
                        <div style={{ marginTop: 8, position: 'relative', borderRadius: 8, overflow: 'hidden', height: 80 }}>
                            <img src={splashUrl} alt="Login screen preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <button onClick={() => setSplashUrl('')}
                                style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 11, padding: '2px 6px' }}>
                                Remove
                            </button>
                        </div>
                    )}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, marginTop: 4 }}>
                    Business info — for estimates &amp; invoices
                </div>
                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 10, marginTop: -6 }}>
                    Not shown in the app itself — only appears on generated PDFs (estimates, invoices). Never returned by the public branding endpoint.
                </div>
                <Field label="Legal business name (falls back to app name if blank)" value={legalName} onChange={setLegalName} />
                <Field label="Address line 1" value={addressLine1} onChange={setAddressLine1} />
                <Field label="Address line 2" value={addressLine2} onChange={setAddressLine2} />
                <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 2 }}><Field label="City" value={bizCity} onChange={setBizCity} /></div>
                    <div style={{ flex: 1 }}><Field label="State" value={bizState} onChange={setBizState} /></div>
                    <div style={{ flex: 1 }}><Field label="ZIP" value={bizPostalCode} onChange={setBizPostalCode} /></div>
                </div>
                <Field label="Business phone" value={bizPhone} onChange={setBizPhone} />
                <Field label="Tax ID / EIN" value={taxId} onChange={setTaxId} placeholder="Sensitive — PDFs and this editor only" />
                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, color: C.textSub, marginBottom: 5 }}>
                        Default contract / disclaimer text — copied onto each new estimate, editable per-estimate after
                    </label>
                    <textarea value={disclaimerText} onChange={e => setDisclaimerText(e.target.value)} rows={4}
                        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.text, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
                </div>

                {error && <ErrorBox msg={error} />}
                {success && <div style={{ color: C.green, fontSize: 13, padding: '8px 12px', background: C.greenBg, borderRadius: 6, marginBottom: 12 }}>{success}</div>}
                <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save branding'}</Btn>
            </Card>

            <div>
                <Card style={{ padding: 16 }}>
                    <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Preview</div>

                    {/* Sidebar/header strip */}
                    <div style={{ background: primary, borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            {logoUrl
                                ? <img src={logoUrl} alt="" style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 4 }} />
                                : <div style={{ width: 22, height: 22, background: 'rgba(255,255,255,0.2)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: navTextColor, fontWeight: 700, fontSize: 11 }}>{appName[0]}</div>}
                            <span style={{ color: navTextColor, fontWeight: 600, fontSize: 12 }}>{appName}</span>
                        </div>
                        {/* Active nav item */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 6px', borderLeft: `3px solid ${accent}`, background: 'rgba(255,255,255,0.12)', borderRadius: '0 4px 4px 0' }}>
                            <div style={{ width: 8, height: 8, borderRadius: 2, background: accent }} />
                            <span style={{ color: navTextColor, fontSize: 10, fontWeight: 600 }}>Active nav item</span>
                        </div>
                        {/* Section label */}
                        <div style={{ marginTop: 6, fontSize: 9, fontWeight: 700, color: accent, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Section label</div>
                    </div>

                    {/* Button */}
                    <div style={{ background: `linear-gradient(135deg, ${buttonColor} 0%, ${secondary} 100%)`, borderRadius: 7, padding: '7px 12px', textAlign: 'center', color: '#fff', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                        Primary button
                    </div>

                    {/* Accent elements row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 11 }}>A</div>
                        <span style={{ fontSize: 11, color: C.textMuted }}>Avatar &amp; accent elements</span>
                        <div style={{ marginLeft: 'auto', border: `1px solid ${accent}`, borderRadius: 5, padding: '3px 8px', fontSize: 10, color: accent, fontWeight: 600 }}>Sign out</div>
                    </div>
                </Card>

                <Card style={{ padding: 16, marginTop: 12 }}>
                    <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tenant info</div>
                    <div style={{ fontSize: 13, color: C.text, marginBottom: 4 }}><strong>Slug:</strong> {tenant.slug}</div>
                    {tenant.domain && <div style={{ fontSize: 13, color: C.text, marginBottom: 4 }}><strong>Domain:</strong> {tenant.domain}</div>}
                    {tenant.supportEmail && <div style={{ fontSize: 13, color: C.text, marginBottom: 4 }}><strong>Support:</strong> {tenant.supportEmail}</div>}
                    <div style={{ fontSize: 13, color: C.text }}><strong>Status:</strong>{' '}
                        <span style={{ color: tenant.isActive ? C.green : C.textMuted }}>{tenant.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                </Card>
            </div>
        </div>
    );
}

// ── Notifications & settings panel ────────────────────────────────────────────

function ConfigPanel({ tenantId }: { tenantId: string }) {
    const [config, setConfig] = useState<TenantConfigDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        setLoading(true);
        platformApi.getTenantConfig(tenantId)
            .then(setConfig)
            .catch(() => setError('Could not load settings.'))
            .finally(() => setLoading(false));
    }, [tenantId]);

    const save = async () => {
        if (!config) return;
        setSaving(true); setError(''); setSuccess('');
        try {
            const updated = await platformApi.updateTenantConfig(tenantId, {
                features: config.features,
                services: config.services,
                payment: config.payment,
                notifications: config.notifications,
                jobFlow: config.jobFlow,
            });
            setConfig(updated);
            setSuccess('Settings saved.');
        } catch (err: any) {
            setError(err.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const ToggleRow = ({ label, description, checked, onChange }: {
        label: string; description: string; checked: boolean; onChange: (v: boolean) => void;
    }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', borderBottom: `1px solid ${C.borderLight}` }}>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 1 }}>{description}</div>
            </div>
            <button onClick={() => { onChange(!checked); setSuccess(''); }}
                style={{ width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                    background: checked ? C.green : '#D1D5DB', position: 'relative', flexShrink: 0, transition: 'background 0.15s' }}>
                <span style={{ position: 'absolute', top: 3, left: checked ? 21 : 3, width: 16, height: 16,
                    borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
            </button>
            <span style={{ fontSize: 11, fontWeight: 600, color: checked ? C.green : C.textMuted, width: 28, textAlign: 'right' }}>
                {checked ? 'On' : 'Off'}
            </span>
        </div>
    );

    if (loading) return <Card><div style={{ color: C.textMuted, fontSize: 13 }}>Loading...</div></Card>;
    if (!config) return <Card><ErrorBox msg={error || 'Could not load settings.'} /></Card>;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Notifications</h3>
                </div>
                <ToggleRow label="Email notifications" description="Send transactional emails for job updates, confirmations, receipts"
                    checked={config.notifications.emailEnabled}
                    onChange={v => setConfig({ ...config, notifications: { ...config.notifications, emailEnabled: v } })} />
                <ToggleRow label="Push notifications" description="Mobile push alerts for job status changes and contractor location"
                    checked={config.notifications.pushEnabled}
                    onChange={v => setConfig({ ...config, notifications: { ...config.notifications, pushEnabled: v } })} />
                <ToggleRow label="SMS notifications" description="Text message alerts as a fallback when push is unavailable"
                    checked={config.notifications.smsEnabled}
                    onChange={v => setConfig({ ...config, notifications: { ...config.notifications, smsEnabled: v } })} />
                <ToggleRow label="Email admins on new customer" description="Send every active admin an email whenever a customer registers"
                    checked={config.notifications.newCustomerAdminEmail}
                    onChange={v => setConfig({ ...config, notifications: { ...config.notifications, newCustomerAdminEmail: v } })} />
            </Card>

            <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Job Flow</h3>
                </div>
                <ToggleRow label="Require photo on completion" description="Contractor must upload a job photo before marking complete"
                    checked={config.jobFlow.requirePhotoOnCompletion}
                    onChange={v => setConfig({ ...config, jobFlow: { ...config.jobFlow, requirePhotoOnCompletion: v } })} />
                <ToggleRow label="Auto-assign jobs" description="Automatically assign nearby available contractors to new jobs"
                    checked={config.jobFlow.autoAssignEnabled}
                    onChange={v => setConfig({ ...config, jobFlow: { ...config.jobFlow, autoAssignEnabled: v } })} />
                <ToggleRow label="Auto-convert approved estimates" description="Skip the manual 'Convert to job' click — an approved estimate becomes a job immediately. Turn on once you trust the Estimate Builder is producing correct estimates."
                    checked={config.jobFlow.autoConvertApprovedEstimates}
                    onChange={v => setConfig({ ...config, jobFlow: { ...config.jobFlow, autoConvertApprovedEstimates: v } })} />
            </Card>

            {error && <ErrorBox msg={error} />}
            {success && <div style={{ color: C.green, fontSize: 13, padding: '8px 12px', background: C.greenBg, borderRadius: 6 }}>{success}</div>}
            <div><Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save settings'}</Btn></div>
        </div>
    );
}

// ── Tenant detail — sidebar + tabs ────────────────────────────────────────────

type TenantTab = 'branding' | 'features' | 'config' | 'admins' | 'analytics';

const TENANT_TABS: { key: TenantTab; label: string }[] = [
    { key: 'branding',  label: 'Branding' },
    { key: 'features',  label: 'Feature Flags' },
    { key: 'config',    label: 'Notifications & Job Flow' },
    { key: 'admins',    label: 'Admins' },
    { key: 'analytics', label: 'Analytics' },
];

function TenantDetailView({ tenant, onSaved, onBack }: { tenant: TenantDetail; onSaved: (u: TenantDetail) => void; onBack: () => void }) {
    const [tab, setTab] = useState<TenantTab>('branding');

    return (
        <div>
            {/* Breadcrumb */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.greenMid, fontSize: 13, fontWeight: 500, padding: 0 }}>← Tenants</button>
                <span style={{ color: C.textMuted }}>/</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{tenant.name}</span>
            </div>

            {/* Sidebar + content */}
            <div style={{ display: 'flex', gap: 20, alignItems: 'start' }}>
                {/* Sidebar */}
                <div style={{ width: 200, flexShrink: 0, background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                    {/* Tenant identity */}
                    <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: tenant.branding.logoUrl ? 'transparent' : tenant.branding.primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                                {tenant.branding.logoUrl
                                    ? <img src={tenant.branding.logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    : <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{tenant.name[0]}</span>}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tenant.name}</div>
                                <div style={{ fontSize: 11, color: C.textMuted }}>{tenant.slug}</div>
                            </div>
                        </div>
                        <div style={{ marginTop: 8, fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 10, display: 'inline-block',
                            background: tenant.isActive ? C.greenBg : '#F5F5F5', color: tenant.isActive ? C.green : C.textMuted }}>
                            {tenant.isActive ? 'Active' : 'Inactive'}
                        </div>
                    </div>

                    {/* Tab nav */}
                    <nav>
                        {TENANT_TABS.map(t => (
                            <button key={t.key} onClick={() => setTab(t.key)}
                                style={{
                                    display: 'block', width: '100%', textAlign: 'left',
                                    padding: '10px 16px', fontSize: 13,
                                    fontWeight: tab === t.key ? 600 : 400,
                                    color: tab === t.key ? C.green : C.text,
                                    background: tab === t.key ? C.greenBg : 'transparent',
                                    border: 'none',
                                    borderLeft: `3px solid ${tab === t.key ? C.green : 'transparent'}`,
                                    cursor: 'pointer',
                                    transition: 'background 0.1s',
                                }}>
                                {t.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {tab === 'branding'  && <BrandingPanel tenant={tenant} onSaved={onSaved} />}
                    {tab === 'features'  && <FeatureFlagsPanel tenantId={tenant.id} />}
                    {tab === 'config'    && <ConfigPanel tenantId={tenant.id} />}
                    {tab === 'admins'    && <AdminsPanel tenantId={tenant.id} tenantName={tenant.name} />}
                    {tab === 'analytics' && <TenantStatsPanel tenantId={tenant.id} />}
                </div>
            </div>
        </div>
    );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

export default function PlatformDashboard() {
    const { admin, logout } = usePlatformAuth();
    const nav = useNavigate();
    const [tenants, setTenants] = useState<TenantDetail[]>([]);
    const [selected, setSelected] = useState<TenantDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showNewTenant, setShowNewTenant] = useState(false);
    const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
    const [selecting, setSelecting] = useState(false);
    // MarketplaceApp isn't tenant-owned (it's a global catalog authored by Platform Admin), so
    // it gets its own top-level section rather than a 6th TENANT_TABS entry.
    const [section, setSection] = useState<'tenants' | 'marketplace' | 'templates' | 'sessions' | 'integrations'>('tenants');
    const [justCreatedTenant, setJustCreatedTenant] = useState<TenantDetail | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const [ts, ps] = await Promise.all([
                platformApi.getTenants(),
                platformApi.getPlatformStats().catch(() => null),
            ]);
            setTenants(ts);
            setPlatformStats(ps);
        } catch (e: any) {
            if (e.message?.includes('401')) { logout(); nav('/platform/login'); return; }
            setError(e.message || 'Failed to load tenants.');
        } finally {
            setLoading(false);
        }
    }, [logout, nav]);

    useEffect(() => { load(); }, [load]);

    const handleTenantCreated = (t: TenantDetail) => {
        setTenants(prev => [...prev, t].sort((a, b) => a.name.localeCompare(b.name)));
        setShowNewTenant(false);
        setJustCreatedTenant(t); // template picker decides when to actually select/open it
    };

    // getTenants() (the list endpoint) omits businessInfo — only the single-tenant detail
    // endpoint returns it. Fetch full detail here rather than reusing the list row, so
    // BrandingPanel always has a real businessInfo object to read from.
    const selectTenant = async (id: string) => {
        setSelecting(true); setError('');
        try {
            setSelected(await platformApi.getTenant(id));
        } catch (e: any) {
            setError(e.message || 'Failed to load tenant.');
        } finally {
            setSelecting(false);
        }
    };

    const fmt$ = (v: number) => '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });

    return (
        <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'Inter', sans-serif" }}>
            {/* Header */}
            <div style={{ background: '#111', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #222' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 28, height: 28, background: C.greenMid, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M7 1L1 4l6 3 6-3-6-3zM1 10l6 3 6-3M1 7l6 3 6-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>Platform Admin</span>
                    <span style={{ color: '#444', fontSize: 14 }}>·</span>
                    <span style={{ color: '#888', fontSize: 13 }}>ServiceTrek</span>
                    <div style={{ display: 'flex', gap: 4, marginLeft: 20 }}>
                        {(['tenants', 'marketplace', 'templates', 'integrations', 'sessions'] as const).map(s => (
                            <button
                                key={s}
                                onClick={() => { setSection(s); setSelected(null); }}
                                style={{
                                    background: section === s ? '#222' : 'none', border: 'none', borderRadius: 6,
                                    color: section === s ? '#fff' : '#888', fontSize: 12.5, fontWeight: 600,
                                    padding: '6px 12px', cursor: 'pointer',
                                }}
                            >
                                {s === 'tenants' ? 'Tenants' : s === 'marketplace' ? 'App Marketplace' : s === 'templates' ? 'Tenant Templates' : s === 'integrations' ? 'Integrations' : 'Active Sessions'}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <span style={{ color: '#888', fontSize: 13 }}>{admin?.name}</span>
                    <button onClick={() => { logout(); nav('/platform/login'); }} style={{ background: 'none', border: '1px solid #333', borderRadius: 6, color: '#aaa', fontSize: 12, padding: '4px 10px', cursor: 'pointer' }}>
                        Sign out
                    </button>
                </div>
            </div>

            <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
                {section === 'marketplace' ? (
                    <MarketplaceCatalogAdmin />
                ) : section === 'templates' ? (
                    <TenantTemplateCatalogAdmin />
                ) : section === 'integrations' ? (
                    <IntegrationsPanel />
                ) : section === 'sessions' ? (
                    <ActiveSessionsPanel />
                ) : selected ? (
                    <TenantDetailView
                        tenant={selected}
                        onSaved={updated => {
                            setSelected(updated);
                            setTenants(prev => prev.map(t => t.id === updated.id ? updated : t));
                        }}
                        onBack={() => setSelected(null)}
                    />
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Tenants</h1>
                            <Btn onClick={() => setShowNewTenant(true)}>+ New tenant</Btn>
                        </div>

                        {/* Platform-wide stats */}
                        {platformStats && (
                            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                                <StatTile label="Tenants" value={`${platformStats.activeTenants} / ${platformStats.totalTenants}`} />
                                <StatTile label="Jobs this month" value={platformStats.totalJobsThisMonth} />
                                <StatTile label="Revenue this month" value={fmt$(platformStats.totalRevenueThisMonth)} />
                                <StatTile label="Contractors" value={platformStats.totalContractors} />
                                <StatTile label="Customers" value={platformStats.totalCustomers} />
                            </div>
                        )}

                        {loading && <div style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>Loading tenants...</div>}
                        {selecting && <div style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>Loading tenant...</div>}
                        {error && <div style={{ color: '#D84040', padding: '12px 16px', background: '#FFF0F0', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

                        {!loading && !error && (
                            <Card style={{ padding: 0, overflow: 'hidden' }}>
                                {tenants.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No tenants found.</div>}
                                {tenants.map(t => <TenantRow key={t.id} tenant={t} onSelect={() => selectTenant(t.id)} />)}
                            </Card>
                        )}
                    </>
                )}
            </div>

            {showNewTenant && <NewTenantModal onCreated={handleTenantCreated} onClose={() => setShowNewTenant(false)} />}
            {justCreatedTenant && (
                <TemplatePickerModal
                    tenant={justCreatedTenant}
                    onDone={() => { setSelected(justCreatedTenant); setJustCreatedTenant(null); }}
                />
            )}
        </div>
    );
}
